import React, { useState, useEffect, ChangeEvent, ReactNode } from 'react';
import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Badge from '@mui/material/Badge';
import TabPanel from 'components/UI/TabPanel';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { Main, Text, Background } from 'styles/themeColors';
import { BorderRadius } from 'styles/themeBorderRadius';
import { MainColors } from 'types/enumTypes';
import { Space } from 'styles/themeSpaces';
import { Height } from 'styles/themeHeights';
import { ReadOnlyWrapper } from 'components/common/ReadOnlyWrapper';

const TabsContainer = styled.div<any>`
  .app-bar {
    background-color: ${Background.white};
    border: none;
    box-shadow: none;
  }
`;

const StyledTabs = muiStyled(Tabs, {
  shouldForwardProp: (prop) => prop !== '$theme',
})<{ $theme?: string }>(({ $theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  borderBottom: '1px solid !important',
  borderColor: `${Main[$theme as keyof typeof Main]} !important`,
  '& .Mui-selected': {
    backgroundColor: `${Main[$theme as keyof typeof Main]} !important`,
  },
}));

const StyledTab = muiStyled(Tab, {
  shouldForwardProp: (prop) => !['$theme', '$active'].includes(prop as string),
})<{ $theme?: string; $active?: boolean; color?: string; borderRadius?: string; padding?: string; margin?: string }>(({ color, borderRadius: br, padding: p, margin: m, $active, $theme }) => ({
  color: `${color} !important`,
  minWidth: '100px !important',
  maxWidth: '300px !important',
  opacity: '1 !important',
  borderRadius: br ? `${br} !important` : `${BorderRadius.m} ${BorderRadius.m} 0 0 !important`,
  paddingTop: p ? `${p} !important` : '0px !important',
  paddingBottom: p ? `${p} !important` : '0px !important',
  margin: m ? `${m} !important` : '0px !important',
  whiteSpace: 'nowrap !important',
  ...(!$active && {
    '&:hover': {
      backgroundColor: $theme === 'accent'
        ? 'rgba(73, 141, 218, 0.1) !important'
        : `${Main['brandLightest']} !important`,
    },
  }),
  '& .MuiTab-wrapper div': {
    alignItems: 'center !important',
  },
}));

// Don't pass color and bgColor props to MaterialUI Badge component to avoid console warnings
const StyledBadge = muiStyled(Badge, {
  shouldForwardProp: (prop) => prop !== 'bgColor',
})<any>(({ bgColor, color }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: bgColor,
    color: color,
    minWidth: '20px',
    position: 'static',
    borderRadius: `${BorderRadius.m} !important`,
    marginLeft: Space.spHalf,
  },
  '& .MuiBadge-anchorOriginTopRightRectangular': {
    transform: 'none !important',
  },
}));

interface TabBadge {
  content: string;
  color: string;
  backgroundColor: string;
};

interface Tab {
  value: string;
  badge: TabBadge;
  disabled?: boolean;
};

interface Props {
  children: any;
  className?: string;
  tabs: Tab[];
  activeTab?: number;
  margin?: string;
  padding?: string;
  height?: string;
  borderRadius?: string;
  tabPanelPadding?: string;
  theme?: MainColors;
  setActiveTab?: (number: number) => void;
  actionButtons?: ReactNode;
  disabledTabTooltip?: string;
};

const TabsComponent = ({
  children,
  className = '',
  tabs,
  activeTab = 0,
  margin = '0',
  padding,
  height = Height.m,
  borderRadius,
  tabPanelPadding,
  theme = 'accent',
  setActiveTab,
  actionButtons = null,
  disabledTabTooltip = ''
}: Props) => {
  const [value, setValue] = useState(0);

  const handleChange = (event: ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
    if (setActiveTab) {
      setActiveTab(newValue);
    }
  };

  useEffect(() => {
    if (activeTab || activeTab === 0) {
      setValue(activeTab);
    }
  }, [activeTab]);

  const renderTabs = () => {
    return tabs.map((tab, index) => {
      const isActive = value === index;
      const isDisabled = tab.disabled;

      const tabContent = (
        <FlexboxContainer>
          {tab.value}
          {tab.badge?.content && (
            <StyledBadge
              badgeContent={tab.badge.content}
              color={tab.badge.color}
              bgColor={tab.badge.backgroundColor}
              classes={{ badge: 'badge' }}
            />
          )}
        </FlexboxContainer>
      );

      return isDisabled ? (
        <ReadOnlyWrapper key={`tab-wrapper-${index}`} readOnly={true} tooltipMessage={disabledTabTooltip} hideTooltip>
          <StyledTab
            className={`tab ${className}`}
            key={index}
            label={tabContent}
            color={Text.secondary}
            $theme={theme}
            padding={padding}
            margin={margin}
            borderRadius={borderRadius}
            $active={false}
            disabled={true}
            {...a11yProps(index)}
          />
        </ReadOnlyWrapper>
      ) : (
        <StyledTab
          className={`tab ${className}`}
          key={index}
          label={tabContent}
          color={isActive ? Text.whitePrimary : Text.secondary}
          $theme={theme}
          padding={padding}
          margin={margin}
          borderRadius={borderRadius}
          $active={isActive}
          onClick={() => setValue(index)}
          {...a11yProps(index)}
        />
      );
    });
  };

  return (
    <TabsContainer>
      <AppBar
        position="static"
        className="app-bar"
        style={{ padding, margin, height }}
      >
        <StyledTabs
          value={value}
          onChange={handleChange}
          aria-label="Tabs"
          TabIndicatorProps={{ style: { display: 'none' } }}
          $theme={theme}
        >
          {renderTabs()}
          {actionButtons && actionButtons}
        </StyledTabs>
      </AppBar>
      {
        children && children.length > 0 ? children.map((child: ReactNode, index: number) => (
          <TabPanel key={index} index={index} value={value} padding={tabPanelPadding}>
            {child}
          </TabPanel>
        )) : <TabPanel key={0} index={0} value={value} padding={tabPanelPadding}>
          {children}
        </TabPanel>
      }
    </TabsContainer>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `company-tab-${index}`,
    'aria-controls': `company-tabpanel-${index}`,
  };
};

export default TabsComponent;
